/* =========================================================================
   WCAG 2.2 SC 2.4.3 Focus Order / SC 4.1.2 Name-Role-Value — WS8.1
   getbreathdeath.com remediation (case 514658-2026).

   The promotional email-capture popup is rendered by Klaviyo at runtime
   (form id UQLd5d; container [data-testid="modal-form-container"]). It is not
   theme Liquid, so it is decorated here from the theme side. Live audit
   (2026-06-18) found the container has NO role, NO aria-modal, and NO
   accessible name, and on dismiss focus lands on Klaviyo's unlabeled "Teaser"
   re-open tab at the bottom of the page rather than returning to where the
   user was. That is exactly the complaint's para 33 barrier:
     - "FULLSCREEN Form" / no purpose announced            -> F-2.4.3-a, F-4.1.2-i
     - focus returns to the page bottom past the footer    -> F-2.4.3-a

   This wrapper, in vanilla JS (no jQuery), watches the body for the popup and:
     - records the user's last focus OUTSIDE the popup as the return target;
     - sets role="dialog" + aria-modal="true" on the popup container;
     - gives it a purpose-conveying accessible name from the offer headline
       (aria-label, derived from the popup's own richtext so it survives copy
       changes), falling back to "Email sign-up offer";
     - moves focus into the popup on open (only if Klaviyo has not already);
     - traps Tab / Shift+Tab inside the popup;
     - closes on Escape by invoking Klaviyo's OWN close control;
     - on dismissal (popup removed or hidden, by any path — X, Escape, the
       decline button, or submit), returns focus to the recorded target rather
       than the teaser / page bottom.

   It NEVER intercepts form submission and only ever invokes Klaviyo's own
   close button, so the vendor form's behavior is untouched.

   Idempotent: the container is flagged with data-wcag-dialog once; the global
   listeners are installed once. A double include or re-run is a no-op.
   ========================================================================= */
(function () {
  'use strict';

  if (window.__wcagKlaviyoDialogInit) {
    return;
  }
  window.__wcagKlaviyoDialogInit = true;

  var POPUP_SEL = '[data-testid="modal-form-container"]';
  var CLOSE_SEL = 'button.klaviyo-close-form, .klaviyo-close-form';
  var TEASER_SEL = '[class*="Teaser"], [class*="teaser"]';
  var FOCUSABLE =
    'a[href], area[href], input:not([disabled]):not([type="hidden"]), ' +
    'select:not([disabled]), textarea:not([disabled]), button:not([disabled]), ' +
    'iframe, [tabindex]:not([tabindex="-1"]), [contenteditable="true"]';

  var returnTarget = null; // element to restore focus to on dismiss
  var activePopup = null; // popup container currently being managed
  var keydownHandler = null;

  function inPopup(el) {
    return !!(el && el.closest && el.closest(POPUP_SEL));
  }

  function visible(el) {
    if (!el || !el.getBoundingClientRect) {
      return false;
    }
    var r = el.getBoundingClientRect();
    if (r.width <= 0 || r.height <= 0) {
      return false;
    }
    var cs = window.getComputedStyle(el);
    return cs.visibility !== 'hidden' && cs.display !== 'none';
  }

  /* Continuously remember the last element the user focused OUTSIDE the popup,
     skipping the body and Klaviyo's teaser/re-open tab, so the return target is
     a real place to send focus back to on dismiss. */
  document.addEventListener(
    'focusin',
    function (e) {
      var t = e.target;
      if (!t || t === document.body || t === document.documentElement) {
        return;
      }
      if (inPopup(t)) {
        return;
      }
      if (t.closest && t.closest(TEASER_SEL)) {
        return;
      }
      returnTarget = t;
    },
    true
  );

  function offerName(popup) {
    var blocks = popup.querySelectorAll('[class*="richtext"], h1, h2, h3, h4');
    for (var i = 0; i < blocks.length; i++) {
      if (blocks[i].closest && blocks[i].closest('button, [role="button"]')) {
        continue;
      }
      var txt = (blocks[i].innerText || blocks[i].textContent || '')
        .replace(/\s+/g, ' ')
        .trim();
      if (txt.length >= 6) {
        return txt.slice(0, 100);
      }
    }
    return '';
  }

  function decorate(popup) {
    if (popup.getAttribute('role') !== 'dialog') {
      popup.setAttribute('role', 'dialog');
    }
    popup.setAttribute('aria-modal', 'true');
    if (
      !popup.getAttribute('aria-label') &&
      !popup.getAttribute('aria-labelledby')
    ) {
      /* aria-label (not aria-labelledby): Klaviyo re-renders the form internals
         on state changes, which would leave a labelledby reference dangling. */
      popup.setAttribute('aria-label', offerName(popup) || 'Email sign-up offer');
    }
    popup.setAttribute('data-wcag-dialog', 'bound');
  }

  function focusables(popup) {
    return Array.prototype.filter.call(
      popup.querySelectorAll(FOCUSABLE),
      function (el) {
        return visible(el);
      }
    );
  }

  function moveFocusIn(popup) {
    if (inPopup(document.activeElement)) {
      return; // Klaviyo already moved focus to its close button
    }
    var f = focusables(popup);
    if (f.length) {
      try {
        f[0].focus();
      } catch (e) {}
    } else {
      popup.setAttribute('tabindex', '-1');
      try {
        popup.focus();
      } catch (e) {}
    }
  }

  function closeButton(popup) {
    return popup.querySelector(CLOSE_SEL) || document.querySelector(CLOSE_SEL);
  }

  function restoreFocus() {
    var t = returnTarget;
    if (!(t && document.contains(t) && visible(t))) {
      /* Fallback: never the body / footer / teaser. Send focus to main content. */
      t =
        document.getElementById('MainContent') ||
        document.querySelector('main, [role="main"]');
      if (t && !t.hasAttribute('tabindex')) {
        t.setAttribute('tabindex', '-1');
      }
    }
    if (t) {
      try {
        t.focus();
      } catch (e) {}
    }
  }

  function teardown() {
    if (keydownHandler) {
      document.removeEventListener('keydown', keydownHandler, true);
      keydownHandler = null;
    }
    activePopup = null;
  }

  function manage(popup) {
    if (activePopup === popup) {
      decorate(popup); // keep attributes present if Klaviyo re-rendered
      return;
    }
    activePopup = popup;
    decorate(popup);
    moveFocusIn(popup);

    keydownHandler = function (e) {
      if (!activePopup || !document.contains(activePopup) || !visible(activePopup)) {
        return;
      }
      if (e.key === 'Escape' || e.key === 'Esc') {
        var btn = closeButton(activePopup);
        if (btn) {
          btn.click(); // invoke Klaviyo's own close; dismissal handler restores focus
        }
        return;
      }
      if (e.key === 'Tab') {
        var f = focusables(activePopup);
        if (!f.length) {
          return;
        }
        var first = f[0];
        var last = f[f.length - 1];
        var act = document.activeElement;
        if (e.shiftKey) {
          if (act === first || !activePopup.contains(act)) {
            e.preventDefault();
            try {
              last.focus();
            } catch (err) {}
          }
        } else if (act === last || !activePopup.contains(act)) {
          e.preventDefault();
          try {
            first.focus();
          } catch (err) {}
        }
      }
    };
    document.addEventListener('keydown', keydownHandler, true);
  }

  function tick() {
    var popup = document.querySelector(POPUP_SEL);
    if (popup && visible(popup)) {
      manage(popup);
    } else if (activePopup) {
      teardown();
      restoreFocus();
    }
  }

  var scheduled = false;
  function schedule() {
    if (scheduled) {
      return;
    }
    scheduled = true;
    var raf =
      window.requestAnimationFrame ||
      function (cb) {
        return window.setTimeout(cb, 16);
      };
    raf(function () {
      scheduled = false;
      tick();
    });
  }

  function start() {
    var obs = new MutationObserver(schedule);
    obs.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['style', 'class']
    });
    tick(); // handle a popup already present at load
  }

  if (document.readyState !== 'loading') {
    start();
  } else {
    document.addEventListener('DOMContentLoaded', start);
  }
})();
